import { memo, useState, useCallback, useRef, useEffect, type MouseEvent as ReactMouseEvent } from 'react'
import {
  Box, IconButton, Tooltip, Divider,
  Collapse, CircularProgress,
} from '@mui/material'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import UndoIcon from '@mui/icons-material/Undo'
import RedoIcon from '@mui/icons-material/Redo'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CodeIcon from '@mui/icons-material/Code'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import ClearAllIcon from '@mui/icons-material/ClearAll'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import FindInPageOutlinedIcon from '@mui/icons-material/FindInPageOutlined'
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import { useAppActions, useNav } from '../AppContext'
import { pmSaveQuestion, pmDelQuestion } from '../services/ProjectManagerService'
import { runScore } from '../services/QuestionActions'
import {
  useQuestionProcessing, setQuestionProcessing,
  clearQuestionProcessing,
} from '../services/TaskManagerService'
import BidQuestionPlanTab from './BidQuestionPlanTab'
import BidQuestionSourcesTab from './BidQuestionSourcesTab'
import BidQuestionStoryboardTab from './BidQuestionStoryboardTab'
import BidQuestionScoringTab from './BidQuestionScoringTab'
import BidQuestionAnswerTab from './BidQuestionAnswerTab'
import { structureToPlainText } from './TaResponse'
import QuestionBadge from './QuestionBadge'
import TabHeader from './TabHeader'
import TaQueryBar from './TaQueryBar'
import TaThreeDots from './TaThreeDots'
import TaActionButton from './TaActionButton'
import GenerateTraceButton from './buttons/GenerateTraceButton'
import GenerateResponseButton from './buttons/GenerateResponseButton'
import GenerateScoringButton from './buttons/GenerateScoringButton'
import TaPadlock from './TaPadlock'
import StatePicker from './StatePicker'
import DebugJsonButton from './DebugJsonButton'
import ConfirmModal from '../tabs/modals/ConfirmModal'
import TaWordLimit from './TaWordLimit'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { FS_XL, ICON_XS, ICON_SM, ICON_LG, BORDER_BLACK } from '../common/theme'
import { copyToClipboard } from '../common/clipboardHelpers'
import { DEBUG_MODE, hasAnswerText, useRenderLog } from '../common/common'
import type { UndoRedoHandle } from '../common/useUndoRedoHistory'
import OpenInBuilderButton from './buttons/OpenInBuilderButton'
import OpenInScoreFixButton from './buttons/OpenInScoreFixButton'
import { Question, questionAnchorId } from '../types'
import { addToast } from '../services/Toasts'

export const QUESTION_PAGE_SIZE = 10

interface BidQuestionProps {
  question: Question
  projectsha: string
  options?: any
  paginStartIndex?: number
  onGenerate?: (q: any) => void
  // When true the component sizes itself to fill its parent (flex column,
  // height 100%) and the collapse toggle is hidden. Used by single-question
  // views like ResponseBuilderTab where there's no point in stacking these.
  // Default is the natural list-style layout used by BidManagerTab's
  // inline question list.
  fillHeight?: boolean
}

type LeftTab = 'plan' | 'sources' | 'storyboard' | 'scoring'
type ViewTab = 'trace' | 'text'

function BidQuestionImpl({
  question,
  projectsha,
  paginStartIndex,
  fillHeight = false,
}: BidQuestionProps) {

  // useRenderLog(`BidQuestion ${question.questionsha.slice(0, 6)}`, {
  //   question, projectsha, paginStartIndex, fillHeight,
  // })

  const { setCurrentProject, setCurrentQuestion, setPendingWwInit, updateQuestion, getCurrentProject } = useAppActions()
  const { setActiveTab } = useNav()
  // Per-section processing flags now live in the TaskManagerService
  // registry instead of on the question itself. Subscribe to this
  // question's slot for reactive reads; write via setQuestionProcessing.
  const processing = useQuestionProcessing(question.questionsha)
  // The question is read directly from the `question` prop, which is sourced
  // from currentProject.questions[] in AppContext. Mutations go back through
  // `updateQuestion(sha, patch)` so every other consumer sees them too -
  // there is no local copy.
  const [collapsed, setCollapsed] = useState(false)
  const [leftTab, setLeftTab] = useState<LeftTab>('plan')
  const [saving, setSaving] = useState(false)
  // Trace / score running flags come from the central TaskManager store -
  // runTrace / runScore set the flag for us, so any consumer (LHS list,
  // toolbar, future widgets) reflects the same state.
  const [leftPct, setLeftPct] = useState(45)
  const [leftCollapsed, setLeftCollapsed] = useState(false)
  // Tweaks modals are now owned by `TaAddElementsButton` (per-tab),
  // which renders both the trigger icon and the modal in one place.
  // Advanced Edit is now a proper tab (`advedit`) under the Builder
  // submenu — we navigate there instead of opening a per-row modal.
  // Single confirm-modal state - `null` closed, else identifies which
  // destructive action is pending. Lets one ConfirmModal drive all clear
  // flows plus the per-question delete.
  const [confirmKind, setConfirmKind] = useState<'workflow' | 'sources' | 'storyboard' | 'score' | 'delete' | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Undo / redo for the question-text TaQueryBar. The buttons live in the
  // main toolbar, the buffer lives inside TaQueryBar - wired via the
  // imperative handle pattern shared with TaResponse.
  const questionEditorRef = useRef<UndoRedoHandle>(null)
  const [questionCanUndo, setQuestionCanUndo] = useState(false)
  const [questionCanRedo, setQuestionCanRedo] = useState(false)

  const index = question.index ?? paginStartIndex ?? 0
  const isprocessing = processing.isprocessing;
  const processingDone = !isprocessing;

  // `viewstring` tells the backend which side of the answer (basic
  // text vs pro trace) is the canonical version. The view is always
  // 'basic' now (there's only one editor pane, with the trace shown
  // as a sim gutter beside it), but we keep the sync callback for
  // the local streaming / workflow-nav call sites — they still fire
  // and this keeps `answerobj.viewstring` current with what the
  // user is seeing.
  const setViewTabAndSync = useCallback((v: ViewTab) => {
    const want = v === 'trace' ? 'pro' : 'basic'
    if (question.answerobj?.viewstring === want) return
    updateQuestion(question.questionsha, {
      answerobj: { ...(question.answerobj ?? {} as any), viewstring: want },
    })
  }, [question.answerobj])

  // Track when a stream finishes so the auto-trace effect below can fire once
  // for that question (the streaming pipeline returns the answer text but not
  // the structured trace).
  const autoTracedShaRef = useRef<string | null>(null)
  const justEndedStreamRef = useRef(false)
  useEffect(() => {
    console.log(`BidQuestion ${question.questionsha.slice(0, 6)} effect[processingDone] fired, processingDone=${processingDone}`)
    if (processingDone) justEndedStreamRef.current = true
  }, [processingDone])

  // When a stream starts, jump to the Plan tab so the user sees the
  // streaming milestones, and switch the answer view to "text" so the
  // streamed tokens are visible (the trace view requires answerstructure
  // which only arrives after the run completes).
  useEffect(() => {
    console.log(`BidQuestion ${question.questionsha.slice(0, 6)} effect[isprocessing] fired, isprocessing=${isprocessing}`)
    if (isprocessing) {
      setLeftTab('plan')
      setViewTabAndSync('text')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isprocessing])

  // Stream-mirror: the modal-batch path leaves the handler before the
  // server actually finishes, so we can't clear `processing.answer` in
  // its finally. Instead watch processingDone - when the question's
  // taskstream pipeline reports done, clear the answer flag so the
  // button / workflow row stops spinning.
  useEffect(() => {
    console.log(`BidQuestion ${question.questionsha.slice(0, 6)} effect[processingDone-2] fired, processingDone=${processingDone}, processing.answer=${processing.answer}`)
    if (processingDone && processing.answer) {
      setQuestionProcessing(question.questionsha, 'answer', false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processingDone])

  const toggleLock = useCallback((lockKey: string) => {
    updateQuestion(question.questionsha, { locks: { ...question.locks, [lockKey]: !(question.locks as any)?.[lockKey] } as any })
  }, [question.locks])

  const handleSave = useCallback(async () => {
    setSaving(true)
    try {
      await pmSaveQuestion({ ...question, projectsha: projectsha })
      updateQuestion(question.questionsha, { modified: false })
      addToast('success', 'Question saved')
    } catch {
      addToast('error', 'Failed to save question')
    } finally {
      setSaving(false)
    }
  }, [question, projectsha, addToast])

  const handleClearAnswer = useCallback(() => {
    // Wipe the per-answer fields locally. We DON'T auto-persist any more -
    // the cleared question is just marked `modified: true` so the toolbar
    // Save button lights up gold and the user commits the clear in the
    // same way they commit any other edit. The streaming-store entry is
    // still reset so the UI doesn't show stale chunks if a stream was
    // just running.
    const clearedPatch: Partial<Question> = {
      answerobj: undefined as any,
      bideval: undefined as any,
      structure: undefined as any,
      sourcedocs: [],
      structuretweaks: [],
      answertweaks: [],
    }
    const cleared: Question = { ...question, ...clearedPatch, modified: true } as Question
    // updateQuestion in AppContext skips undefined keys - go directly via
    // setCurrentProject to actually wipe them.
    setCurrentProject(prev => {
      if (!prev?.questions) return prev
      const idx = prev.questions.findIndex((qq: any) => qq.questionsha === question.questionsha)
      if (idx < 0) return prev
      const newQs = [...prev.questions]
      newQs[idx] = cleared
      return { ...prev, questions: newQs }
    })
    setCurrentQuestion(cleared)
    clearQuestionProcessing(cleared.questionsha)
    autoTracedShaRef.current = null
    justEndedStreamRef.current = false
    addToast('info', 'Workflow cleared. Save to persist')
  }, [question, addToast, setCurrentProject, setCurrentQuestion])

  // Section-clear helper - wipes the listed fields locally (bypassing
  // updateQuestion which ignores `undefined`) and persists immediately.
  // `savePatch` is what gets sent to the server; callers pass explicit
  // `null`s there so JSON.stringify keeps the keys and the backend actually
  // clears them (it would otherwise leave the old values in place).
  const wipeFieldsAndSave = useCallback(async (patch: Partial<Question>, savePatch: Record<string, any>, toastOk: string, toastErr: string) => {
    const cleared = { ...question, ...patch, modified: false } as Question
    setCurrentProject(prev => {
      if (!prev?.questions) return prev
      const idx = prev.questions.findIndex((qq: any) => qq.questionsha === question.questionsha)
      if (idx < 0) return prev
      const newQs = [...prev.questions]
      newQs[idx] = cleared
      return { ...prev, questions: newQs }
    })
    setCurrentQuestion(cleared)
    try {
      await pmSaveQuestion({ ...cleared, ...savePatch, projectsha: projectsha })
      addToast('success', toastOk)
    } catch {
      addToast('error', toastErr)
    }
  }, [question, projectsha, addToast, setCurrentProject, setCurrentQuestion])

  const handleClearSources = useCallback(() => {
    wipeFieldsAndSave({ sourcedocs: [] }, { sourcedocs: [] }, 'Sources cleared', 'Failed to clear sources')
  }, [wipeFieldsAndSave])

  // Per-source delete / restore - no immediate persist, the question's
  // `modified` flag flips and the user clicks Save (or another action that
  // saves) to commit. The Sources tab pairs delete with a 6s undo banner
  // using these as its delete + restore primitives.
  const handleDeleteSource = useCallback((i: number) => {
    const next = [...(question.sourcedocs ?? [])]
    next.splice(i, 1)
    updateQuestion(question.questionsha, { sourcedocs: next })
  }, [question.sourcedocs])

  const handleRestoreSource = useCallback((i: number, doc: any) => {
    const next = [...(question.sourcedocs ?? [])]
    next.splice(i, 0, doc)
    updateQuestion(question.questionsha, { sourcedocs: next })
  }, [question.sourcedocs])

  const handleClearStoryboard = useCallback(() => {
    wipeFieldsAndSave(
      { structure: undefined as any, structuretweaks: [] },
      { structure: null, structuretweaks: [] },
      'Storyboard cleared',
      'Failed to clear storyboard',
    )
  }, [wipeFieldsAndSave])

  const handleClearScore = useCallback(() => {
    wipeFieldsAndSave(
      { bideval: undefined as any },
      { bideval: null },
      'Score cleared',
      'Failed to clear score',
    )
  }, [wipeFieldsAndSave])

  // Delete this question from the bid. Removes it from currentProject.questions
  // via setCurrentProject (so every consumer sees the change) then persists
  // server-side. If the deleted question was the focused one we also clear
  // currentQuestion so dependent surfaces don't keep pointing at a ghost.
  const handleDelete = useCallback(async () => {
    const sha = question.questionsha
    setCurrentProject(prev => {
      if (!prev?.questions) return prev
      return {
        ...prev,
        questions: prev.questions.filter((qq: any) => qq.questionsha !== sha),
        modified: true,
      } as any
    })
    setCurrentQuestion(prev => (prev && (prev as any).questionsha === sha ? null : prev))
    try {
      await pmDelQuestion(sha, projectsha)
      addToast('success', 'Question deleted')
    } catch {
      addToast('error', 'Failed to delete question')
    }
  }, [question.questionsha, projectsha, setCurrentProject, setCurrentQuestion])

  const handleScore = useCallback(async () => {
    // runScore owns the processing-registry flip for 'scoring' (and the
    // updateQuestion patch on success), so this handler stays thin.
    try {
      var project = getCurrentProject();
      var success = false;
      if (project) {
        const result = await runScore(question, project, updateQuestion)
        success = result?.success;
      }
      if (!project || !success) addToast('error', 'Failed to estimate score')
    } catch {
      addToast('error', 'Failed to score question')
    }
  }, [question, projectsha, updateQuestion])

  const handleResizeStart = useCallback((e: ReactMouseEvent) => {
    e.preventDefault()
    const container = containerRef.current
    if (!container) return
    const onMove = (ev: MouseEvent) => {
      const rect = container.getBoundingClientRect()
      const pct = ((ev.clientX - rect.left) / rect.width) * 100
      setLeftPct(Math.max(20, Math.min(80, pct)))
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [])

  const threedroplist = [
    { label: 'Duplicate', icon: <ContentCopyIcon sx={{ fontSize: ICON_SM }} />, onClick: () => { } },
    { label: 'Clear Workflow', icon: <ClearAllIcon sx={{ fontSize: ICON_SM }} />, onClick: () => setConfirmKind('workflow'), danger: true, dividerAbove: true },
    { label: 'Delete Question', icon: <DeleteOutlineIcon sx={{ fontSize: ICON_SM }} />, onClick: () => setConfirmKind('delete'), danger: true },
  ]

  const threedotsAnswer = [
    { label: 'Copy HTML', icon: <CodeIcon sx={{ fontSize: ICON_SM }} />, onClick: () => copyToClipboard(structureToPlainText(question.answerobj?.answerstructure)) },
    { label: 'Export', icon: <FileDownloadOutlinedIcon sx={{ fontSize: ICON_SM }} />, onClick: () => { } },
    { label: 'Clear Answer', icon: <ClearAllIcon sx={{ fontSize: ICON_SM }} />, onClick: handleClearAnswer, danger: true, dividerAbove: true },
  ]

  // Per-tab "filled" (the step's data is present) and "processing" (the
  // backend is currently producing it) flags. The processing flags read
  // the registry snapshot first (the unified per-section flag), then
  // fall back to the inferred isStreaming / tracing / scoring signals
  // so existing taskstream-driven activity still lights up rows for
  // questions that haven't been touched by a click yet.
  const sourcesDone = (question.sourcedocs?.length ?? 0) > 0
  const structureDone = !!question.structure
  const rawScoreVal = (question.bideval as any)?.scoreint
  const scoreVal: number | null = typeof rawScoreVal === 'number' && rawScoreVal >= 0 ? rawScoreVal : null
  const scoreDone = scoreVal != null
  const answerDone = hasAnswerText(question)
  const sourcesProcessing = !!processing.sourcedocs || (isprocessing && !sourcesDone)
  const structureProcessing = !!processing.structure || (isprocessing && sourcesDone && !structureDone)
  const answerProcessing = !!processing.answer || (isprocessing && structureDone && !answerDone)
  const traceProcessing = processing.isprocessingtrace
  const scoringProcessing = processing.isprocessingscoring

  const LEFT_TABS: { key: LeftTab; label: string; filled?: boolean; processing?: boolean }[] = [
    { key: 'plan', label: 'Workflow' },
    { key: 'sources', label: 'Sources', filled: sourcesDone, processing: sourcesProcessing },
    { key: 'storyboard', label: 'Storyboard', filled: structureDone, processing: structureProcessing },
    { key: 'scoring', label: 'Est. Score', filled: scoreDone, processing: scoringProcessing },
  ]

  return (
    <Box
      id={questionAnchorId(question.questionsha)}
      sx={{
        borderBottom: 1, borderColor: 'divider', scrollMarginTop: 4,
        // In fill-height mode the component becomes a vertical flex column
        // that takes the whole parent - used by single-question views like
        // ResponseBuilderTab. The default behaviour (natural height) is
        // what the inline question list uses.
        ...(fillHeight ? {
          display: 'flex', flexDirection: 'column',
          height: '100%', minHeight: 0, overflow: 'hidden',
        } : null),
      }}
    >

      {/* Sticky header */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider',
        pt: 0.25
      }}>

        {/* Main toolbar */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 0.75, py: 0.4, gap: 0.5, flexWrap: 'wrap' }}>
          {/* Collapse toggle - only useful in the list view. Hidden when
              the parent has asked us to fill its container (single-question
              view), since there's nothing to collapse alongside. */}
          {!fillHeight && (
            <Tooltip title={collapsed ? 'Expand' : 'Collapse'}>
              <IconButton size="small" onClick={() => setCollapsed(v => !v)} sx={{ p: 0 }}>
                {collapsed
                  ? <ExpandMoreIcon sx={{ fontSize: FS_XL }} />
                  : <ExpandLessIcon sx={{ fontSize: FS_XL }} />}
              </IconButton>
            </Tooltip>
          )}
          {/* Padlock - locks the question text itself (gates TaQueryBar's
              `editable` below via question.locks.question). */}
          <TaPadlock
            isLocked={!!question.locks?.question}
            onToggle={() => toggleLock('question')}
            sx={{ px: 0, ml: '-2px' }}
          />
          <QuestionBadge
            numbering={question.numbering}
            fallback={index + 1}
            testIdPrefix="pmq_badge"
            onCommit={async (v: string) => {
              if (v === (question.numbering ?? '')) return
              updateQuestion(question.questionsha, { numbering: v })
              try {
                await pmSaveQuestion({ ...question, numbering: v, projectsha: projectsha })
                updateQuestion(question.questionsha, { modified: false })
              } catch {
                addToast('error', 'Failed to save numbering')
              }
            }}
          />



          <Box sx={{ flex: 1 }} />

          {/* <Typography variant="caption" sx={{ opacity: 0.5, fontSize: '0.7rem' }}>Answer Size</Typography> */}
          <TaWordLimit
            replylimit={question.replylimit}
            onChange={(rl) => updateQuestion(question.questionsha, { replylimit: rl })}
          />

          <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

          <GenerateResponseButton question={question} />

          <GenerateTraceButton question={question} />

          <GenerateScoringButton question={question} />

          <OpenInBuilderButton question={question} />

          <OpenInScoreFixButton question={question} />

          <TaActionButton
            title={saving ? 'Saving...' : question.modified ? 'Save (unsaved changes)' : 'No changes to save'}
            onClick={handleSave}
            disabled={!question.modified}
            running={saving}
            icon={<SaveOutlinedIcon sx={{ fontSize: ICON_SM }} />}
            color={question.modified ? 'primary.main' : undefined}
            testId="pmq_save_btn"
          />

          {/* Undo / Redo for the question-text editor below. The buffer
              lives inside TaQueryBar; these buttons drive it via the
              imperative handle. */}
          <TaActionButton
            title="Undo question edit"
            onClick={() => questionEditorRef.current?.undo()}
            disabled={!questionCanUndo}
            icon={<UndoIcon sx={{ fontSize: ICON_SM }} />}
            testId="pmq_question_undo_btn"
          />
          <TaActionButton
            title="Redo question edit"
            onClick={() => questionEditorRef.current?.redo()}
            disabled={!questionCanRedo}
            icon={<RedoIcon sx={{ fontSize: ICON_SM }} />}
            testId="pmq_question_redo_btn"
          />

          <StatePicker
            state={question.state ?? 'todo'}
            onChange={(s) => updateQuestion(question.questionsha, { state: s })}
          />

          {DEBUG_MODE && (
            <DebugJsonButton
              value={question}
              title={`Question JSON · ${question.questionsha}`}
              tooltip="Inspect question JSON (debug)"
              testIdPrefix="pmq_debug"
            />
          )}

          <TaThreeDots droplist={threedroplist} index={index} />
        </Box>

        {/* Question text - editable via TaQueryBar so users can refine the
            question inline. Persists on save (the toolbar Save button) and
            propagates immediately to the parent via onQuestionChange. */}
        <Box sx={{ px: 1, pb: 0.75 }}>
          <TaQueryBar
            htmlContent={question.question ?? ''}
            onChange={(html) => updateQuestion(question.questionsha, { question: html })}
            editable={!question.locks?.question}
            placeholder="Enter your question here"
            id={`pmqquestion_${index}`}
            handleRef={questionEditorRef}
            onHistoryChange={({ canUndo, canRedo }) => {
              setQuestionCanUndo(canUndo)
              setQuestionCanRedo(canRedo)
            }}
          />
        </Box>
      </Box>

      {/* Collapsible body. When `fillHeight` is on we tell the Collapse and
          its internal wrappers to flex-fill so the body takes the remaining
          column height instead of the default natural `minHeight: 240`. */}
      <Collapse
        in={!collapsed}
        sx={fillHeight ? {
          flex: 1, minHeight: 0, display: 'flex',
          '& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner': {
            flex: 1, minHeight: 0, display: 'flex',
          },
        } : undefined}
      >
        <Box
          ref={containerRef}
          sx={{
            display: 'flex',
            ...(fillHeight ? { flex: 1, minHeight: 0 } : { minHeight: 240 }),
          }}
        >

          {/* Left panel - hidden when collapsed */}
          {!leftCollapsed && (
            <Box sx={{ width: `${leftPct}%`, display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider', minWidth: 0 }}>

              {/* Left tab bar */}
              <TabHeader
                tabs={LEFT_TABS.map(({ key, label, filled, processing }) => ({
                  key,
                  label,
                  trailing: filled === undefined
                    ? undefined
                    : <TaActionButton
                      indicator
                      running={!!processing}
                      icon={<CheckCircleOutlineIcon sx={{ fontSize: ICON_XS, color: 'success.main' }} />}
                      disabledIcon={<RadioButtonUncheckedIcon sx={{ fontSize: ICON_XS, opacity: 0.3 }} />}
                      disabled={!filled}
                      iconSize={ICON_XS}
                    />,
                }))}
                active={leftTab}
                onChange={setLeftTab}
                fullWidth
                wrap
                endSlot={
                  <Tooltip title="Hide side panel" placement="left">
                    <IconButton
                      data-testid="pmq_left_collapse_btn"
                      size="small"
                      onClick={() => setLeftCollapsed(true)}
                      sx={{ p: 0.25 }}
                    >
                      <ChevronLeftIcon sx={{ fontSize: ICON_SM }} />
                    </IconButton>
                  </Tooltip>
                }
                testIdPrefix="pmq_left"
              />

              {/* Left content */}
              {leftTab === 'plan' ? (
                <BidQuestionPlanTab
                  question={question}
                  isStreaming={isprocessing}
                  tracing={traceProcessing}
                  scoring={scoringProcessing}
                  generating={answerProcessing}
                  onNavigate={(target) => {
                    if (target === 'sources') setLeftTab('sources')
                    else if (target === 'storyboard') setLeftTab('storyboard')
                    else if (target === 'score') setLeftTab('scoring')
                    else if (target === 'answer') setViewTabAndSync('text')
                    // Trace target now maps to the Answer view — the
                    // Trace tab was folded into the sim gutter that
                    // sits beside the Answer editor. No separate view
                    // to flip to; the gutter is on by default.
                    else if (target === 'trace') setViewTabAndSync('text')
                  }}
                />
              ) : (
                <Box sx={{ flex: 1, overflowY: 'auto', p: 1, minHeight: 0 }}>
                  {leftTab === 'sources' && (
                    <BidQuestionSourcesTab
                      question={question}
                      isStreaming={sourcesProcessing}
                      isLocked={question.locks?.sources ?? false}
                      onToggleLock={() => toggleLock('sources')}
                      onClearSources={() => setConfirmKind('sources')}
                      onDeleteSource={handleDeleteSource}
                      onRestoreSource={handleRestoreSource}
                    />
                  )}
                  {leftTab === 'storyboard' && (
                    <BidQuestionStoryboardTab
                      question={question}
                      index={index}
                      onStructureChange={(html) => updateQuestion(question.questionsha, { structure: html })}
                      isLocked={question.locks?.structure ?? false}
                      onToggleLock={() => toggleLock('structure')}
                      onClear={() => setConfirmKind('storyboard')}

                    />
                  )}
                  {leftTab === 'scoring' && (
                    <BidQuestionScoringTab
                      question={question}
                      onScore={handleScore}
                      isLocked={question.locks?.scoring ?? false}
                      onToggleLock={() => toggleLock('scoring')}
                      onClear={() => setConfirmKind('score')}
                    />
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Resize handle (when expanded) / re-expand strip (when collapsed) */}
          {leftCollapsed ? (
            <Box
              sx={{
                width: 34,
                flexShrink: 0,
                borderRight: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.25,
                pt: 0.5,
                bgcolor: 'action.hover',
              }}
            >
              <Tooltip title="Show side panel" placement="right">
                <IconButton
                  data-testid="pmq_left_expand_btn"
                  size="small"
                  onClick={() => setLeftCollapsed(false)}
                  sx={{ p: 0.25 }}
                >
                  <ChevronRightIcon sx={{ fontSize: ICON_SM }} />
                </IconButton>
              </Tooltip>
              <Divider flexItem sx={{ mx: 0.5, my: 0.25 }} />
              {/* Vertical mini-tab strip. Same filled / processing state
                  the expanded TabHeader trailing indicator shows, so the
                  user can still tell what's done at a glance while the
                  panel is minimised. Clicking uncollapses and jumps
                  straight to that tab. */}
              {([
                { key: 'sources' as LeftTab, label: 'Sources', filled: sourcesDone, processing: sourcesProcessing,
                  icon: <FindInPageOutlinedIcon sx={{ fontSize: ICON_SM }} /> },
                { key: 'storyboard' as LeftTab, label: 'Storyboard', filled: structureDone, processing: structureProcessing,
                  icon: <AutoStoriesOutlinedIcon sx={{ fontSize: ICON_SM }} /> },
                { key: 'scoring' as LeftTab, label: 'Est. Score', filled: scoreDone, processing: scoringProcessing,
                  icon: <AssessmentOutlinedIcon sx={{ fontSize: ICON_SM }} /> },
              ]).map(({ key, label, filled, processing, icon }) => (
                <Tooltip title={label} placement="right" key={key}>
                  <IconButton
                    data-testid={`pmq_left_mini_${key}`}
                    size="small"
                    onClick={() => { setLeftTab(key); setLeftCollapsed(false) }}
                    sx={{ p: 0.25 }}
                  >
                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                      {icon}
                      <Box sx={{
                        position: 'absolute',
                        top: -4, right: -4,
                        bgcolor: 'background.paper',
                        borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        lineHeight: 0,
                      }}>
                        {processing
                          ? <CircularProgress size={10} thickness={5} />
                          : filled
                            ? <CheckCircleOutlineIcon sx={{ fontSize: 10, color: 'success.main' }} />
                            : <RadioButtonUncheckedIcon sx={{ fontSize: 10, opacity: 0.35 }} />
                        }
                      </Box>
                    </Box>
                  </IconButton>
                </Tooltip>
              ))}
            </Box>
          ) : (
            <Box
              onMouseDown={handleResizeStart}
              sx={{
                width: 7,
                flexShrink: 0,
                cursor: 'col-resize',
                borderLeft: 1,
                borderRight: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <MoreVertIcon sx={{ fontSize: ICON_LG, opacity: 0.35 }} />
            </Box>
          )}

          {/* Right panel - extracted Answer component, shared with
              ResponseBuilderTab. */}
          <BidQuestionAnswerTab
            question={question}
            isStreaming={answerProcessing}
            tracing={traceProcessing}
            scoring={scoringProcessing}
            isLocked={question.locks?.answer ?? false}
            onToggleLock={() => toggleLock('answer')}
            onOpenWordWizard={() => {
              setPendingWwInit({
                inputtext: structureToPlainText(question.answerobj?.answerstructure),
                replylimit: question.replylimit,
                modsentences: null,
                referral: 'pm',
                wwp: {
                  answerobj: question.answerobj,
                  sequenceids: (question.sourcedocs ?? []).map((d: any) => d.sequencesha).filter(Boolean),
                  projectsha: projectsha,
                  questionsha: question.questionsha,
                },
              })
              setActiveTab('wordcount')
            }}
            threeDotsItems={threedotsAnswer}
            editorId={`pmqanswer_${index}`}
          />
        </Box>
      </Collapse>

      {/* Tweaks pickers were mounted here before; they now live inside
          `TaAddElementsButton`, co-located with the trigger in the tab
          toolbar (BidQuestionStoryboardTab / BidQuestionAnswerTab). */}


      {/* Shared confirm dialog for the four destructive clear actions -
          one mount, switched by `confirmKind`. */}
      {(() => {
        // One lookup table for the confirm modal - keeps the dialog markup
        // tidy and avoids hunting through ternaries when a new kind is added.
        const confirmCopy: Record<NonNullable<typeof confirmKind>, { title: string; body: string; okLabel: string }> = {
          workflow: { title: 'Clear workflow?', body: 'This wipes sources, storyboard, answer, trace and score for this question.', okLabel: 'Clear' },
          sources: { title: 'Clear sources?', body: 'This removes every source attached to the question.', okLabel: 'Clear' },
          storyboard: { title: 'Clear storyboard?', body: 'This removes the storyboard outline and any tweaks.', okLabel: 'Clear' },
          score: { title: 'Clear score?', body: 'This removes the estimated score for this question.', okLabel: 'Clear' },
          delete: { title: 'Delete question?', body: 'This permanently removes the question from the bid. This cannot be undone.', okLabel: 'Delete' },
        }
        const copy = confirmKind ? confirmCopy[confirmKind] : null
        const handleConfirm = () => {
          if (confirmKind === 'workflow') handleClearAnswer()
          else if (confirmKind === 'sources') handleClearSources()
          else if (confirmKind === 'storyboard') handleClearStoryboard()
          else if (confirmKind === 'score') handleClearScore()
          else if (confirmKind === 'delete') handleDelete()
        }
        return (
          <ConfirmModal
            open={!!confirmKind}
            title={copy?.title ?? ''}
            body={copy?.body}
            okLabel={copy?.okLabel ?? 'OK'}
            onConfirm={handleConfirm}
            onClose={() => setConfirmKind(null)}
            testIdPrefix="pmq_clear_confirm"
          />
        )
      })()}
    </Box>
  )
}
//this makes sure it only renders on important things
export const BidQuestion = memo(BidQuestionImpl, (prev, next) =>
  prev.question === next.question &&
  prev.projectsha === next.projectsha &&
  prev.paginStartIndex === next.paginStartIndex &&
  prev.fillHeight === next.fillHeight,
)

